import { TWidget } from 'widgets/Widget';

function ProductFeaturesClassCreator(Widget: TWidget) {
    class ProductFeatures extends Widget {
        activeIndex = -1;

        openedHotspotIndex = -1;

        isClosing = false;

        prefs() {
            return {
                activeClass: 'm-active',
                hiddenClass: 'm-hidden',
                zoomScale: 2,
                ...super.prefs()
            };
        }

        init() {
            super.init();

            this.resetState();
        }

        handleHotspotClick(el) {
            if (this.isClosing) {
                return;
            }

            const index = Number(el.data('index'));

            if (Number.isNaN(index)) {
                return;
            }

            if (this.openedHotspotIndex === index) {
                this.closeDetails();

                return;
            }

            this.openDetails(index);
        }

        handleNext() {
            this.changeSlide(1);
        }

        handlePrevious() {
            this.changeSlide(-1);
        }

        changeSlide(direction: number) {
            if (this.activeIndex < 0 || this.isClosing) {
                return;
            }

            const hotspots = this.getHotspots();

            if (!hotspots.length) {
                return;
            }

            this.activeIndex = (this.activeIndex + direction + hotspots.length) % hotspots.length;

            this.updateSlides();
            this.updateImagePosition();
        }

        openDetails(index: number) {
            this.activeIndex = index;
            this.openedHotspotIndex = index;

            this.updateSlides();
            this.updateHotspots();

            this.ref('details').addClass(this.prefs().activeClass);

            this.updateImagePosition();
            this.setTabsVisible(false);
        }

        closeDetails() {
            const scene = this.ref('scene').get();

            this.isClosing = true;

            this.ref('details').removeClass(this.prefs().activeClass);

            this.setTabsVisible(true);
            this.resetHotspots();

            if (!scene) {
                this.finishClose();

                return;
            }

            scene.style.transform = 'scale(1)';

            const handleTransitionEnd = (event: TransitionEvent) => {
                if (event.target !== scene || event.propertyName !== 'transform') {
                    return;
                }

                scene.removeEventListener('transitionend', handleTransitionEnd);

                this.finishClose();
            };

            scene.addEventListener('transitionend', handleTransitionEnd);
        }

        finishClose() {
            this.activeIndex = -1;
            this.openedHotspotIndex = -1;
            this.isClosing = false;

            this.clearSlides();
            this.resetScene();
        }

        getHotspots() {
            const container = this.ref('hotspots').get();

            if (!container) {
                return [];
            }

            return Array.from(container.children) as Array<HTMLElement>;
        }

        updateHotspots() {
            this.getHotspots().forEach((hotspot, index) => {
                const isActive = index === this.openedHotspotIndex;

                const isHidden = this.openedHotspotIndex >= 0 && !isActive;

                hotspot.classList.toggle(this.prefs().activeClass, isActive);

                hotspot.classList.toggle(this.prefs().hiddenClass, isHidden);
            });
        }

        resetHotspots() {
            this.getHotspots().forEach(hotspot => {
                hotspot.classList.remove(this.prefs().activeClass, this.prefs().hiddenClass);
            });
        }

        setTabsVisible(isVisible: boolean) {
            const widgetRoot = this.ref('self').get();

            if (!widgetRoot) {
                return;
            }

            const productFeatures = widgetRoot.closest('.b-product_features');

            if (!productFeatures) {
                return;
            }

            const tabs = productFeatures.querySelector('.b-product_features-tabs');

            if (!tabs) {
                return;
            }

            tabs.classList.toggle(this.prefs().hiddenClass, !isVisible);
        }

        updateSlides() {
            const slidesContainer = this.ref('slides').get();

            if (!slidesContainer) {
                return;
            }

            Array.from(slidesContainer.children).forEach((slide, index) => {
                slide.classList.toggle(this.prefs().activeClass, index === this.activeIndex);
            });
        }

        clearSlides() {
            const slidesContainer = this.ref('slides').get();

            if (!slidesContainer) {
                return;
            }

            Array.from(slidesContainer.children).forEach(slide => {
                slide.classList.remove(this.prefs().activeClass);
            });
        }

        updateImagePosition() {
            if (this.activeIndex < 0) {
                return;
            }

            const scene = this.ref('scene').get();
            const hotspot = this.getHotspots()[this.activeIndex];

            if (!scene || !hotspot) {
                return;
            }

            const zoomX = Number(hotspot.dataset.zoomX);
            const zoomY = Number(hotspot.dataset.zoomY);

            if (Number.isNaN(zoomX) || Number.isNaN(zoomY)) {
                return;
            }

            scene.style.transformOrigin = `${zoomX}% ${zoomY}%`;
            scene.style.transform = `scale(${this.prefs().zoomScale})`;
        }

        resetScene() {
            const scene = this.ref('scene').get();

            if (!scene) {
                return;
            }

            scene.style.transform = '';
            scene.style.transformOrigin = '';
        }

        resetState() {
            this.activeIndex = -1;
            this.openedHotspotIndex = -1;
            this.isClosing = false;

            this.resetHotspots();
            this.setTabsVisible(true);

            this.ref('details').removeClass(this.prefs().activeClass);

            this.clearSlides();
            this.resetScene();
        }
    }

    return ProductFeatures;
}

export type TProductFeatures = ReturnType<typeof ProductFeaturesClassCreator>;

export type TProductFeaturesInstance = InstanceType<TProductFeatures>;

export default ProductFeaturesClassCreator;
